import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Home, Users, BookOpen, FileText, Crown, BarChart3, Layers, Calendar, TrendingUp, Layout, Newspaper, Play } from 'lucide-react';
import { classNames, cn, animations } from '../../theme/utils';

const MobileMenu = ({ isOpen, onClose, user, onSignOut }) => {
  const location = useLocation();

  const isActive = (path) => {
    // Handle exact match for home page
    if (path === '/') {
      return location.pathname === '/';
    }
    // Handle startsWith for other paths
    return location.pathname.startsWith(path);
  };

  const navigationItems = [
    { path: '/', label: 'Home', icon: Home, showWhen: 'always' },
    // Try Demo hidden for the time being — route/page still exists, just add
    // this line back to restore the nav entry.
    // { path: '/demo', label: 'Try Demo', icon: Play, showWhen: 'unauthenticated' },
    { path: '/leagues', label: 'Leagues', icon: Users, showWhen: 'authenticated' },
    // Blog tab hidden for the time being — route/page still exist, just add
    // this line back to restore the nav entry.
    // { path: '/blog', label: 'Blog', icon: BookOpen, showWhen: 'always' },
    { path: '/rules', label: 'Game Rules', icon: FileText, showWhen: 'always' },
    { path: '/fixtures', label: 'Fixtures', icon: Crown, showWhen: 'always' },
    { path: '/stats', label: 'Stats', icon: BarChart3, showWhen: 'always' },
    // Player Tiers standalone page hidden for the time being — route still
    // exists, just add this line back to restore the nav entry.
    // { path: '/player-tiers', label: 'Player Tiers', icon: Layers, showWhen: 'always' },
    { path: '/fdr', label: 'FDR Calendar', icon: Calendar, showWhen: 'always' },
    { path: '/projections', label: 'Player Projections', icon: TrendingUp, showWhen: 'always' },
    { path: '/lineups', label: 'Predicted Lineups', icon: Layout, showWhen: 'always' },
    { path: '/player-news', label: 'Player News', icon: Newspaper, showWhen: 'always' },
  ];

  const filteredItems = navigationItems.filter(item =>
    item.showWhen === 'always' ||
    (item.showWhen === 'authenticated' && user) ||
    (item.showWhen === 'unauthenticated' && !user)
  );

  // Handle escape key and prevent body scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Add event listeners
    document.addEventListener('keydown', handleEscape);

    // Prevent body scroll when menu is open
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden"
        onClick={onClose}
      />
      
      {/* Mobile Menu */}
      <div className={cn(
        'fixed top-0 right-0 h-full w-80 max-w-[90vw] bg-neutral-900 border-l border-neutral-800 z-50 lg:hidden',
        animations.entrance.slideInRight
      )}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-800">
          <h2 className="text-lg font-semibold text-neutral-50">Menu</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-neutral-800 transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5 text-neutral-400" />
          </button>
        </div>

        {/* User Info */}
        {user && (
          <div className="p-4 border-b border-neutral-800">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">
                  {(user?.user_metadata?.full_name || user?.email || 'U')[0].toUpperCase()}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-neutral-50 truncate">
                  {user?.user_metadata?.full_name || user?.user_metadata?.username || 'User'}
                </p>
                {user?.email && !user.email.endsWith('@local.app') && (
                  <p className="text-xs text-neutral-400 truncate">{user.email}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Navigation Items */}
        <nav className="flex-1 px-4 py-6">
          <ul className="space-y-2">
            {filteredItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={onClose}
                    className={cn(
                      'flex items-center space-x-3 px-3 py-3 rounded-lg transition-colors',
                      active
                        ? 'bg-emerald-500 text-white'
                        : 'text-neutral-400 hover:text-neutral-50 hover:bg-neutral-800'
                    )}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Bottom Actions */}
        <div className="p-4 border-t border-neutral-800">
          {user ? (
            <div className="space-y-2">
              <Link
                to="/account"
                onClick={onClose}
                className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-neutral-400 hover:text-neutral-50 hover:bg-neutral-800 rounded-lg transition-colors"
              >
                Account Settings
              </Link>
              <button
                onClick={() => {
                  onSignOut();
                  onClose();
                }}
                className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-neutral-400 hover:text-neutral-50 hover:bg-neutral-800 rounded-lg transition-colors"
              >
                Sign Out
              </button>
            </div>
          ) : (
            <Link
              to="/auth"
              onClick={onClose}
              className={cn(
                classNames.button.primary,
                'w-full flex items-center justify-center px-4 py-3 text-sm font-medium rounded-lg'
              )}
            >
              Sign In
            </Link>
          )}
        </div>
      </div>
    </>
  );
};

export default MobileMenu;